import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import { getIsContributorOrAdmin } from '../../../bundles/auth';

import {
  getLayout,
  hasBeenUpdated,
  isImportedLayout,
  resetLayout,
  saveLayout,
  updateLayout,
  copyLayout,
  notifyPageChanges,
} from '../../../bundles/layouts';
import { getActiveSite } from '../../../bundles/sites';
import { updateTrail } from '../../../bundles/trails';
import { sanitizeFileName } from '../../../utils';

import Heading from '../../Heading';

import { routeTrail, getNewUrl, parseLayoutPath } from './routeTrails';
import LayoutContent from './widgets/LayoutContent';
import UpdateWidget from './widgets/UpdateWidget';
import './Forms.scss';
import CloseImportButton from './widgets/CloseImportButton';
import createRouteTrail from './trailConfig';

const crypto = globalThis.crypto || globalThis.msCrypto;
const array = new Uint32Array(1);

const LayoutView = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname: url } = useLocation();

  const params = useParams();
  const {
    column: initialColumnIndex,
    page: initialPageIndex,
    panel: initialPanelIndex,
    section: initialSectionIndex,
  } = parseLayoutPath(params['*']);

  const site = useSelector(getActiveSite);
  const layout = useSelector((state) => getLayout(state, site.id));
  const updated = useSelector((state) => hasBeenUpdated(state, site.id));
  const isContributorOrAdmin = useSelector((state) => getIsContributorOrAdmin(state, site));
  const isImported = useSelector(isImportedLayout);

  const { pages = [] } = layout;
  const [activeTree, setActiveTree] = useState(false);
  const defaultSetIsPublic = pages.map((page, index) => ({
    ...page,
    isPublic: page.isPublic ?? true,
    pageIndex: index,
  }));
  layout.pages = [...defaultSetIsPublic];
  const routeTrails = useMemo(() => createRouteTrail(activeTree, setActiveTree), [activeTree]);

  const setTrail = () => {
    if (activeTree) {
      const page = pages[activeTree.page];
      const trail = routeTrail({
        routeTrails,
        context: {
          page,
          activeTree,
        },
        url,
      });
      dispatch(updateTrail(trail));
    }
  };

  useEffect(() => {
    setActiveTree({
      page: initialPageIndex ? Number(initialPageIndex) : undefined,
      section: initialSectionIndex ? Number(initialSectionIndex) : undefined,
      column: initialColumnIndex ? Number(initialColumnIndex) : undefined,
      panel: initialPanelIndex ? Number(initialPanelIndex) : undefined,
    });
    if (pages.length > 0) setTrail();
  }, []);

  useEffect(() => {
    if (pages.length > 0) setTrail();
  }, [params]);

  useEffect(() => {
    if (activeTree) {
      navigate(getNewUrl({ activeTree, url }), { replace: true });
    }
  }, [activeTree]);

  useEffect(() => {
    if (!activeTree || activeTree.page === undefined) {
      return;
    }

    if (activeTree.page >= pages.length) {
      setActiveTree((current) => {
        if (!current || current.page === undefined || current.page < pages.length) {
          return current;
        }

        return {
          ...current,
          page: undefined,
          section: undefined,
          column: undefined,
          panel: undefined,
        };
      });
    }
  }, [activeTree, pages.length]);

  const updatePages = useCallback(
    (callback) => {
      const updatedPages = callback(pages);
      dispatch(updateLayout(site.id, { ...layout, pages: updatedPages }));
    },
    [JSON.stringify(pages), JSON.stringify(layout), site.id]
  );

  const updatePage = useCallback(
    (pageIndex, callback) => {
      const page = layout.pages[pageIndex];
      const updatedPage = callback(page);

      const layoutCopy = structuredClone(layout);
      layoutCopy.pages[pageIndex] = updatedPage;

      dispatch(updateLayout(site.id, layoutCopy));
    },
    [JSON.stringify(layout), site.id]
  );

  const exportLayout = () => {
    const element = document.createElement('a');
    const file = new Blob([JSON.stringify(layout)], { type: 'text/json' });
    element.href = URL.createObjectURL(file);
    element.download = `${sanitizeFileName(site.name)}_layout.txt`;
    document.body.append(element);
    element.click();
  };

  const updateSection = useCallback(
    (pageIndex, sectionIndex, callback) => {
      const page = layout.pages[pageIndex];
      const section = page.sections[sectionIndex];
      const updatedSection = callback(section);
      const layoutCopy = structuredClone(layout);

      layoutCopy.pages[pageIndex].sections[sectionIndex] = updatedSection;

      dispatch(updateLayout(site.id, layoutCopy));
    },
    [JSON.stringify(layout), site.id]
  );

  const updateColumn = useCallback(
    (pageIndex, sectionIndex, columnIndex, callback) => {
      const page = layout.pages[pageIndex];
      const section = page.sections[sectionIndex];
      const column = section.columns[columnIndex];
      const updatedColumn = callback(column);
      const layoutCopy = structuredClone(layout);

      layoutCopy.pages[pageIndex].sections[sectionIndex].columns[columnIndex] = updatedColumn;

      dispatch(updateLayout(site.id, layoutCopy));
      setTrail();
    },
    [JSON.stringify(layout), site.id]
  );

  const onSaveLayout = useCallback(() => {
    dispatch(saveLayout(site.id, layout));
    setTrail();
  }, [site.id, layout]);

  const setActiveTreeIndexes = (update = {}) => {
    const { page, section, column, panel, initial = false } = update;
    setActiveTree(() => ({
      page,
      section,
      column,
      panel,
      initial,
    }));
  };

  const createPage = () => {
    updatePages((existingPages) => [
      ...existingPages,
      {
        id: `page-${uuidv4()}`,
        title: 'Untitled page',
        path: `path-${Math.floor(crypto.getRandomValues(array)[0] * 100_000)}`,
        icon: 'he-voltage',
        sections: [],
      },
    ]);
    dispatch(notifyPageChanges('addPage'));
  };

  const applyImport = (importedLayout) => {
    dispatch(
      updateLayout(
        site.id,
        {
          ...importedLayout,
          id: layout.id,
          site_id: layout.site_id,
        },
        true
      )
    );
  };

  const duplicatePages = (index) => {
    const dup = {
      ...pages[index],
      id: `page-${uuidv4()}`,
      path: `path-${Math.floor(crypto.getRandomValues(array)[0] * 100_000)}`,
      isPublic: false,
    };
    const layoutCopy = structuredClone(layout);
    layoutCopy.pages.splice(index + 1, 0, dup);
    dispatch(copyLayout(site.id, layoutCopy));
  };
  const changeIsPublic = (page) => {
    const updatedPages = pages.map((p) => (p === page ? { ...p, isPublic: !p.isPublic } : p));
    dispatch(updateLayout(site.id, { ...layout, pages: updatedPages }));
  };

  return (
    <div className={`layout-view ${updated ? 'updated' : ''}`}>
      <Heading
        contentLeft={<div className="title">Layout</div>}
        contentRight={
          <CloseImportButton
            createPage={createPage}
            applyImport={applyImport}
            exportLayout={exportLayout}
            activeTree={activeTree}
            setActiveTree={setActiveTree}
            pages={pages}
            isPublic={pages[activeTree.page]?.isPublic}
            changeIsPublic={changeIsPublic}
            isContributorOrAdmin={isContributorOrAdmin}
          />
        }
      />

      <UpdateWidget
        updated={updated}
        isImported={isImported}
        onClick={() => dispatch(resetLayout(site.id))}
        onSaveLayout={onSaveLayout}
      />

      <LayoutContent
        activeTree={activeTree}
        layout={layout}
        pages={pages}
        setActiveTreeIndexes={setActiveTreeIndexes}
        updateColumn={updateColumn}
        updatePage={updatePage}
        updatePages={updatePages}
        updateSection={updateSection}
        duplicatePages={duplicatePages}
      />
    </div>
  );
};

export default LayoutView;
